import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Eye, Sparkles, CheckCircle, PlayCircle, HelpCircle, Zap, Activity, Radio, Pause, Play, AlertCircle } from "lucide-react";
import DetailedInsightsModal from "@/components/DetailedInsightsModal";
import FeedbackButtons from "@/components/FeedbackButtons";
import AgentQuestionCard from "@/components/AgentQuestionCard";

const MAX_UNACKNOWLEDGED_ITEMS = 6;

const HeadsUpDisplay = ({ userId, onRefresh }) => {
  const [updates, setUpdates] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [needsAcknowledgment, setNeedsAcknowledgment] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all"); // all, news, research, actions
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Count unacknowledged (new) items
  const unacknowledgedCount = updates.filter(u => u.status === "new").length;

  // Filter updates based on active filter
  const filteredUpdates = updates.filter(update => {
    if (activeFilter === "all") return true;
    if (activeFilter === "news") return update.priority === "news";
    if (activeFilter === "research") return update.priority === "research";
    if (activeFilter === "actions") return ["prospect", "strategy", "competitor"].includes(update.priority);
    return true;
  });

  // Get counts for each category
  const newsCounts = updates.filter(u => u.priority === "news").length;
  const researchCounts = updates.filter(u => u.priority === "research").length;
  const actionsCounts = updates.filter(u => ["prospect", "strategy", "competitor"].includes(u.priority)).length;

  useEffect(() => {
    // Check if we've hit the limit
    if (unacknowledgedCount >= MAX_UNACKNOWLEDGED_ITEMS) {
      setNeedsAcknowledgment(true);
    }
  }, [unacknowledgedCount]);

  useEffect(() => {
    // Generate autonomous activity every 90-150 seconds (slowed to half cadence)
    const generateActivity = async () => {
      // Don't generate if paused or needs acknowledgment
      if (isPaused || needsAcknowledgment) {
        return;
      }
      
      setIsGenerating(true);
      try {
        const response = await axios.post(`${API}/agent/auto-activity/${userId}`);
        
        const activityData = response.data.data;
        const activityType = activityData.type;
        
        // Map to color and icon based on 4 core functions + question
        let priority, icon;
        if (activityType === "question") {
          priority = "question";
          icon = "?";
        } else if (activityType === "prospect") {
          priority = "prospect";
          icon = "✓";
        } else if (activityType === "research") {
          priority = "research";
          icon = "🔬";
        } else if (activityType === "news") {
          priority = "news";
          icon = "📰";
        } else if (activityType === "strategy") {
          priority = "strategy";
          icon = "⚠";
        } else {
          priority = "competitor";
          icon = "⚡";
        }
        
        const activityUpdate = {
          text: activityData.summary,
          hasButton: activityType !== "question",
          priority: priority,
          icon: icon,
          fullData: activityData,
          type: activityType,
          status: "new",
          viewedAt: null,
          executedAt: null,
          question: activityType === "question" ? {
            text: activityData.question || activityData.summary,
            type: activityData.questionType || "short",
            hint: activityData.hint,
            options: activityData.options || [],
            context: activityData.context
          } : null,
          questionId: activityData.questionId
        };
        
        setUpdates(prev => {
          const newUpdates = [activityUpdate, ...prev.slice(0, 11)];
          // Check if this pushes us to the limit
          const newUnacknowledgedCount = newUpdates.filter(u => u.status === "new").length;
          if (newUnacknowledgedCount >= MAX_UNACKNOWLEDGED_ITEMS) {
            setNeedsAcknowledgment(true);
          }
          return newUpdates;
        });
      } catch (error) {
        console.error("Error generating activity:", error);
      }
      setIsGenerating(false);
    };
    
    // Clear existing intervals/timeouts
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    // Only start generating if not paused and doesn't need acknowledgment
    if (!isPaused && !needsAcknowledgment) {
      timeoutRef.current = setTimeout(generateActivity, 3000);
      intervalRef.current = setInterval(() => {
        const randomDelay = 90000 + Math.random() * 60000;
        setTimeout(generateActivity, randomDelay);
      }, 120000);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [userId, isPaused, needsAcknowledgment]);

  const handlePauseToggle = () => {
    setIsPaused(prev => !prev);
    if (isPaused) {
      toast.success("Autonomous stream resumed");
    } else {
      toast.info("Autonomous stream paused");
    }
  };

  const handleAcknowledge = () => {
    // Mark all "new" items as "viewed"
    setUpdates(prev => prev.map(item => 
      item.status === "new" ? { ...item, status: "viewed", viewedAt: new Date().toISOString() } : item
    ));
    setNeedsAcknowledgment(false);
    toast.success("Stream acknowledged - generating new insights");
  };

  const handleReview = (update, index) => {
    setUpdates(prev => prev.map((item, idx) => 
      idx === index ? { ...item, status: item.status === "executed" ? "executed" : "viewed", viewedAt: new Date().toISOString() } : item
    ));
    setSelectedActivity(update.fullData);
    setSelectedIndex(index);
    setShowDetailedView(true);
  };

  const handleStrategyExecuted = () => {
    if (selectedIndex !== null) {
      setUpdates(prev => prev.map((item, idx) => 
        idx === selectedIndex ? { ...item, status: "executed", executedAt: new Date().toISOString() } : item
      ));
    }
  };

  const handleModalClose = () => {
    setShowDetailedView(false);
    setSelectedActivity(null);
    setSelectedIndex(null);
  };

  const getPriorityStyles = (priority, status) => {
    if (status === "viewed") {
      return {
        border: "border-l-gray-600",
        bg: "bg-gray-800/20",
        text: "text-gray-400",
        icon: "text-gray-500",
        label: getOriginalLabel(priority) + " (Viewed)",
        emoji: getOriginalEmoji(priority),
        glowColor: "rgba(100,100,100,0.2)",
        statusIcon: CheckCircle,
        statusColor: "text-gray-500"
      };
    }
    
    if (status === "executed") {
      return {
        border: "border-l-gray-700",
        bg: "bg-gray-900/30",
        text: "text-gray-500",
        icon: "text-gray-600",
        label: getOriginalLabel(priority) + " (Executed)",
        emoji: getOriginalEmoji(priority),
        glowColor: "rgba(0,255,148,0.1)",
        statusIcon: PlayCircle,
        statusColor: "text-neon-green"
      };
    }
    
    switch (priority) {
      case "question":
        return {
          border: "border-l-purple-500/70",
          bg: "bg-purple-500/5",
          text: "text-purple-400/80",
          icon: "text-purple-400/70",
          label: "Agent Needs Input",
          emoji: "❓",
          glowColor: "rgba(147,51,234,0.15)",
          statusIcon: HelpCircle,
          statusColor: "text-purple-400/70"
        };
      case "competitor":
        return {
          border: "border-l-red-500/70",
          bg: "bg-red-500/5",
          text: "text-red-400/80",
          icon: "text-red-400/70",
          label: "Competitor Threat",
          emoji: "🎯",
          glowColor: "rgba(239,68,68,0.15)",
          statusIcon: null,
          statusColor: null
        };
      case "strategy":
        return {
          border: "border-l-orange-500/70",
          bg: "bg-orange-500/5",
          text: "text-orange-400/80",
          icon: "text-orange-400/70",
          label: "Sales Strategy",
          emoji: "💡",
          glowColor: "rgba(249,115,22,0.15)",
          statusIcon: null,
          statusColor: null
        };
      case "research":
        return {
          border: "border-l-blue-500/70",
          bg: "bg-blue-500/5",
          text: "text-blue-400/80",
          icon: "text-blue-400/70",
          label: "Deep Research",
          emoji: "🔬",
          glowColor: "rgba(59,130,246,0.15)",
          statusIcon: null,
          statusColor: null
        };
      case "news":
        return {
          border: "border-l-cyan-500/70",
          bg: "bg-cyan-500/5",
          text: "text-cyan-400/80",
          icon: "text-cyan-400/70",
          label: "Breaking News",
          emoji: "📰",
          glowColor: "rgba(6,182,212,0.15)",
          statusIcon: null,
          statusColor: null
        };
      case "prospect":
        return {
          border: "border-l-emerald-500/70",
          bg: "bg-emerald-500/5",
          text: "text-emerald-400/80",
          icon: "text-emerald-400/70",
          label: "New Leads",
          emoji: "✅",
          glowColor: "rgba(16,185,129,0.15)",
          statusIcon: null,
          statusColor: null
        };
      default:
        return {
          border: "border-l-gray-500/70",
          bg: "bg-gray-500/5",
          text: "text-gray-300",
          icon: "text-gray-400",
          label: "Update",
          emoji: "→",
          glowColor: "rgba(100,100,100,0.1)",
          statusIcon: null,
          statusColor: null
        };
    }
  };

  const getOriginalLabel = (priority) => {
    switch (priority) {
      case "question": return "Agent Needs Input";
      case "competitor": return "Competitor Threat";
      case "strategy": return "Sales Strategy";
      case "research": return "Deep Research";
      case "news": return "Breaking News";
      case "prospect": return "New Leads";
      default: return "Update";
    }
  };

  const getOriginalEmoji = (priority) => {
    switch (priority) {
      case "question": return "❓";
      case "competitor": return "🎯";
      case "strategy": return "💡";
      case "research": return "🔬";
      case "news": return "📰";
      case "prospect": return "✅";
      default: return "→";
    }
  };

  const handleQuestionAnswered = (questionId, answer) => {
    setUpdates(prev => prev.map(item => 
      item.questionId === questionId 
        ? { ...item, status: "answered" } 
        : item
    ));
  };

  return (
    <motion.div 
      className="glass-card p-6 h-[600px] flex flex-col relative overflow-hidden"
      data-testid="heads-up-display"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" />

      {/* Header */}
      <div className="mb-6 relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Animated Icon */}
            <div className="p-3 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/10 border border-neon-cyan/20">
              <Activity className="h-6 w-6 text-neon-cyan" />
            </div>
            
            <div>
              <h2 className="text-xl text-white flex items-center gap-3 tracking-tight">
                <span className="text-neon-cyan">Autonomous Stream</span>
                {/* Live/Paused indicator */}
                {isPaused ? (
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/30">
                    <Pause className="h-3 w-3 text-amber-400" />
                    <span className="text-xs text-amber-400 font-medium">PAUSED</span>
                  </div>
                ) : needsAcknowledgment ? (
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-purple-500/10 border border-purple-500/30">
                    <AlertCircle className="h-3 w-3 text-purple-400" />
                    <span className="text-xs text-purple-400 font-medium">WAITING</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-neon-green/10 border border-neon-green/30">
                    <Radio className="h-3 w-3 text-neon-green" />
                    <span className="text-xs text-neon-green font-medium">LIVE</span>
                  </div>
                )}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {isGenerating ? (
                  <span className="flex items-center gap-2">
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Zap className="h-3 w-3 text-neon-cyan" />
                    </motion.span>
                    Generating insights...
                  </span>
                ) : isPaused ? (
                  "Paused · Click play to resume"
                ) : needsAcknowledgment ? (
                  `${unacknowledgedCount} items need review`
                ) : (
                  "Active · Running continuously"
                )}
              </p>
            </div>
          </div>
          
          {/* Pause/Play Button */}
          <Button
            onClick={handlePauseToggle}
            variant="ghost"
            size="sm"
            className={`rounded-full p-2 ${
              isPaused 
                ? "bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30" 
                : "bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30"
            }`}
            data-testid="stream-pause-btn"
          >
            {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </Button>
        </div>
        
        {/* Acknowledgment Banner */}
        {needsAcknowledgment && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-purple-400" />
              <div>
                <p className="text-sm text-purple-300">Stream paused - {unacknowledgedCount} unreviewed insights</p>
                <p className="text-xs text-purple-400/70">Review or acknowledge to continue receiving new insights</p>
              </div>
            </div>
            <Button
              onClick={handleAcknowledge}
              size="sm"
              className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40"
              data-testid="acknowledge-stream-btn"
            >
              Acknowledge All
            </Button>
          </motion.div>
        )}
        
        {/* Category Filter Tabs */}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={() => setActiveFilter("all")}
            variant="ghost"
            size="sm"
            className={`rounded-full px-3 py-1 text-xs ${
              activeFilter === "all"
                ? "bg-white/10 text-white border border-white/20"
                : "text-gray-500 hover:text-white hover:bg-white/5"
            }`}
            data-testid="filter-all"
          >
            All ({updates.length})
          </Button>
          <Button
            onClick={() => setActiveFilter("news")}
            variant="ghost"
            size="sm"
            className={`rounded-full px-3 py-1 text-xs ${
              activeFilter === "news"
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                : "text-gray-500 hover:text-cyan-400 hover:bg-cyan-500/10"
            }`}
            data-testid="filter-news"
          >
            📰 News ({newsCounts})
          </Button>
          <Button
            onClick={() => setActiveFilter("research")}
            variant="ghost"
            size="sm"
            className={`rounded-full px-3 py-1 text-xs ${
              activeFilter === "research"
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                : "text-gray-500 hover:text-blue-400 hover:bg-blue-500/10"
            }`}
            data-testid="filter-research"
          >
            🔬 Research ({researchCounts})
          </Button>
          <Button
            onClick={() => setActiveFilter("actions")}
            variant="ghost"
            size="sm"
            className={`rounded-full px-3 py-1 text-xs ${
              activeFilter === "actions"
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                : "text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10"
            }`}
            data-testid="filter-actions"
          >
            ⚡ Actions ({actionsCounts})
          </Button>
        </div>
      </div>

      {/* Updates Stream */}
      <ScrollArea className="flex-1 pr-4 mb-6 relative z-10">
        <div className="space-y-4">
          <AnimatePresence>
            {filteredUpdates.length > 0 ? (
              filteredUpdates.map((update, idx) => {
                const styles = getPriorityStyles(update.priority, update.status);
                const StatusIcon = styles.statusIcon;
                
                // Render question card for question type
                if (update.type === "question" && update.question && update.status !== "answered") {
                  return (
                    <motion.div
                      key={`question-${idx}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <AgentQuestionCard
                        question={update.question}
                        userId={userId}
                        questionId={update.questionId || `q-${idx}`}
                        onAnswered={handleQuestionAnswered}
                      />
                    </motion.div>
                  );
                }
                
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    whileHover={{ scale: 1.01, x: 4 }}
                    className={`glass-card-light border-l-4 ${styles.border} rounded-r-xl p-4 transition-all cursor-pointer ${update.status !== "new" ? "opacity-70" : ""}`}
                    style={{ 
                      boxShadow: update.status === "new" ? `0 4px 20px ${styles.glowColor}` : "none"
                    }}
                    data-testid={`hud-update-${idx}`}
                  >
                    {/* Category Label with Status */}
                    <div className="flex items-center justify-between mb-3">
                      <motion.span 
                        className={`text-xs uppercase tracking-wider ${styles.text} flex items-center gap-2`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <span className="text-base">{styles.emoji}</span>
                        {styles.label}
                      </motion.span>
                      
                      {StatusIcon && update.status !== "new" && (
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 ${styles.statusColor}`}>
                          <StatusIcon className="h-3 w-3" />
                          <span className="text-xs">
                            {update.status === "executed" ? "Executed" : update.status === "answered" ? "Answered" : "Viewed"}
                          </span>
                        </div>
                      )}
                      
                      {update.status === "new" && (
                        <div className="px-2 py-0.5 rounded-full bg-neon-cyan/10 border border-neon-cyan/20">
                          <span className="text-xs text-neon-cyan/80">NEW</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${update.status === "new" ? "text-gray-200" : "text-gray-400"} leading-relaxed`}>
                          {update.text}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Feedback Buttons */}
                        <FeedbackButtons
                          userId={userId}
                          agentType="autonomous_stream"
                          actionId={update.fullData?.actionId}
                          outputSummary={update.text}
                          context={{ type: update.type }}
                        />
                        {update.hasButton && (
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                            <Button
                              onClick={() => handleReview(update, idx)}
                              className={`${
                                update.status === "new" 
                                  ? "bg-neon-pink hover:bg-neon-pink/90" 
                                  : "bg-gray-700 hover:bg-gray-600"
                              } text-white text-xs px-3 py-1.5 h-auto rounded-full border-0 transition-all`}
                              data-testid={`review-btn-${idx}`}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Review
                            </Button>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <motion.div 
                className="text-center py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="inline-block"
                >
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-neon-cyan/10 to-neon-pink/10 border border-white/10 inline-block">
                    <Sparkles className="h-12 w-12 text-neon-cyan" />
                  </div>
                </motion.div>
                <p className="text-gray-400 mt-6 font-medium">
                  {activeFilter === "all" 
                    ? "Autonomous agent initializing..." 
                    : `No ${activeFilter} items yet`}
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  {activeFilter === "all" 
                    ? "First activity will appear shortly" 
                    : "Items will appear here as they're generated"}
                </p>
                
                {/* Loading dots */}
                <div className="flex justify-center gap-1.5 mt-4">
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 rounded-full bg-neon-cyan"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ScrollArea>

      {/* Detailed Insights Modal */}
      <DetailedInsightsModal
        isOpen={showDetailedView}
        onClose={handleModalClose}
        activityData={selectedActivity}
        userId={userId}
        onStrategyExecuted={handleStrategyExecuted}
      />
    </motion.div>
  );
};

export default HeadsUpDisplay;
